import React, { Suspense, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Center, Loading } from '@cotera/client/app/components';
import { CredentialForm } from '@cotera/client/app/components/tools';
import { ProviderAccountCards } from '@cotera/client/v0/components/tool-connections/provider-account-cards';
import { useProviderAccounts } from '@cotera/client/v0/components/tool-connections/hooks';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import type { WebhookProviderCredentials } from '@cotera/client/app/etc/api/typed-client';
import { WebhookProviderIcon } from '../webhook-provider-icons';
import { WebhookConfigCard } from './webhook-config-card';
import type { WebhookProviderCatalogEntry } from '../types';

type WebhookProviderCredentialSectionProps = {
  catalog: WebhookProviderCatalogEntry;
  /** Notifies the parent whether the credential is connected (for gating). */
  onConnectedChange?: (connected: boolean) => void;
};

const LoadingCard: React.FC = () => (
  <div className="rounded-md border bg-muted/20 p-3">
    <Center className="py-2">
      <Loading.Dots />
    </Center>
  </div>
);

/**
 * For providers that declare a `provider_account` credential requirement (e.g.
 * WorkOS needs an API key so Cotera can register the webhook and read back its
 * signing secret), this renders the connect/entry step before the rest of the
 * setup. Connection state comes from the shared tools-v2 provider-account cache
 * (`useProviderAccounts`), so it refreshes off the same `ToolCredentialUpdated`
 * broadcast the rest of the tool-connection UI uses.
 */
export const WebhookProviderCredentialSection: React.FC<
  WebhookProviderCredentialSectionProps
> = ({ catalog, onConnectedChange }) => {
  const credentials = catalog.credentials;
  if (credentials?.kind !== 'provider_account') {
    return null;
  }
  return (
    <Suspense fallback={<LoadingCard />}>
      <WebhookProviderCredentialSectionInner
        catalog={catalog}
        credentials={credentials}
        onConnectedChange={onConnectedChange}
      />
    </Suspense>
  );
};

const WebhookProviderCredentialSectionInner: React.FC<{
  catalog: WebhookProviderCatalogEntry;
  credentials: WebhookProviderCredentials;
  onConnectedChange?: (connected: boolean) => void;
}> = ({ catalog, credentials, onConnectedChange }) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const { data } = useProviderAccounts(credentials.providerKey);
  const connected = data.accounts.length > 0;

  useEffect(() => {
    onConnectedChange?.(connected);
  }, [connected, onConnectedChange]);

  const connect = useMutation({
    mutationFn: async (values: Record<string, string>) => {
      const result = await client.webhookProviders.connectCredentials({
        providerKey: catalog.key,
        credentials: values,
      });
      if (result.isErr()) {
        throw new Error(
          result.error.t === 'application'
            ? result.error.err.message
            : 'Failed to save credentials'
        );
      }
      return result.value;
    },
    onSuccess: (result) => {
      // Same mechanism the tool-connection UI uses: broadcast a credential
      // update so every reader of the tools-v2 provider caches refreshes.
      emitToolCredentialUpdated(orgId, result.providerKey);
    },
  });

  if (connected) {
    return (
      <ProviderAccountCards
        provider={credentials.providerKey}
        accounts={data.accounts}
      />
    );
  }

  return (
    <WebhookConfigCard
      icon={<WebhookProviderIcon providerKey={catalog.key} />}
      title={`Connect ${catalog.displayName}`}
      description={`Enter your ${catalog.displayName} credentials so Cotera can install the webhook.`}
    >
      <CredentialForm
        provider={catalog.key}
        strategyName="user_provided"
        fields={[...credentials.fields]}
        docsUrl={credentials.docsUrl ?? null}
        variant="compact"
        submitLabel="Connect"
        onSubmit={async (values) => {
          await connect.mutateAsync(values);
        }}
      />
    </WebhookConfigCard>
  );
};
